import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import { usePageFlow } from '@hooks/usePageFlow'
import { usePathBuilder } from '@hooks/usePathBuilder'
import { useAppNavigate } from '@hooks/useAppNavigate'
import { initClaimStatus } from '@redux/slices/claimStatusSlice'
import { CustomerFacingClaimRecordStatus } from '@typesApp/ClaimRecord'
import { useLazyGetClaimRecordQuery } from '@redux/queries/claimRecords'
import { resetLastForm } from '@redux/slices/formsSlice'
import { closeApp, unhandledError } from '@redux/slices/workflowSlice'
import { getCustomerFacingStatus } from '@utils/claimUtil'
import { Skeleton } from '@components/ui-css/Skeleton'
import { DrawerContentBody, DrawerContentFooter, DrawerContentHeader } from '@components/ui-css/Drawer'
import { LoadingSkeleton } from './LoadingSkeleton'

type ResumeState = {
  claimRecordId?: string
}

export const ResumeRedirect: React.FC = () => {
  const [isResume, setResume] = useState(false)
  const buildPath = usePathBuilder()
  const navigate = useAppNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const { defaultView, getResumeToView } = usePageFlow()
  const [fetchClaimRecord, isLoading] = useLazyGetClaimRecordQuery()

  const state: ResumeState = location.state

  useEffect(() => {
    if (!state?.claimRecordId) {
      setResume(false)
      return
    }

    setResume(true)

    return () => {
      setResume(false)
    }
  }, [state])

  useEffect(() => {
    if (!isResume || !state.claimRecordId) return

    fetchClaimRecord(state.claimRecordId)
      .unwrap()
      .then(claimRecord => {
        dispatch(resetLastForm())
        dispatch(initClaimStatus(claimRecord))

        // resume to view configured in pageflow
        const status = getCustomerFacingStatus(claimRecord)

        if (status === CustomerFacingClaimRecordStatus.IN_PROGRESS) {
          const resumeToView = getResumeToView(claimRecord)
          if (resumeToView) {
            navigate(buildPath(resumeToView))
            return
          }
        }

        // otherwise navigate to default view
        navigate(buildPath(defaultView))
      })
      .catch(error => {
        console.warn('Error resuming claim application', error)
        dispatch(closeApp())

        // TODO: add listener for unhandled error to show generic error modal
        dispatch(unhandledError(error))
      })
      .finally(() => {
        setResume(false)
      })
  }, [dispatch, isResume, state])

  return isLoading ? <LoadingSkeleton /> : null
}
